import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/** Agregované server metriky z `/admin/api/v1/stats` (cached 2s server-side). */
export interface ServerStats {
  timestamp: string;
  throughput: {
    requestsPerSecond: number;
    totalRequests: number;
    last1m: number;
    last5m: number;
    last1h: number;
    history: { timestamp: string; count: number; requestsPerSecond: number }[];
  };
  latency: {
    total: LatencyBucket;
    connector: LatencyBucket;
    processing: LatencyBucket;
  };
  pools: {
    database: { active: number; idle: number; waiting: number; total: number; max: number; utilizationPercent: number };
    http: { leased: number; available: number; pending: number; max: number; utilizationPercent: number };
  };
  errors: {
    totalErrors: number;
    totalSuccess: number;
    errorRatePercent: number;
    byStatusCode: Record<string, number> | null;
    recentErrors: RecentError[] | null;
  };
  jvm: {
    memory: { heapUsed: number; heapMax: number; heapUtilizationPercent: number; nonHeapUsed: number };
    cpu: { processCpuUsage: number; systemCpuUsage: number; availableProcessors: number };
    threads: { live: number; daemon: number; peak: number };
    gc: { pauseCount: number; pauseTotalMs: number; lastPauseMs: number };
  };
}

export interface RecentError {
  timestamp: string;
  endpoint: string | null;
  statusCode: number | null;
  errorMessage: string | null;
}

export interface LatencyBucket {
  p50: number;
  p95: number;
  p99: number;
  min: number;
  max: number;
  avg: number;
  count: number;
}

/** Živé metriky — volitelný auto-refresh interval (ms; 0 = manuální). */
export function useServerStats(refetchIntervalMs = 5000) {
  return useQuery({
    queryKey: ["stats", "server"],
    queryFn: () => apiFetch<ServerStats>("/admin/api/v1/stats"),
    staleTime: 0,
    refetchInterval: refetchIntervalMs > 0 ? refetchIntervalMs : false,
  });
}
